import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  WaterDrop as HeatIcon,
  EnergySavingsLeaf as EcoIcon,
  Savings as SavingsIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { quotesApi } from '../services/api.ts';
import { QuoteStatus, QUOTE_STATUS_LABELS, QUOTE_STATUS_COLORS } from '../types/index.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const fmt = (n?: number | null, decimals = 2) =>
  n !== undefined && n !== null ? n.toFixed(decimals) : '—';

const fmtCurrency = (n?: number | null) =>
  n !== undefined && n !== null
    ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => {
        const field = d.loc?.slice(1).join(' → ') || '';
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .join('; ');
  }
  return String(detail || 'An unexpected error occurred');
};

const QuotePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const quoteId = Number(id);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState('');

  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => quotesApi.getQuote(quoteId),
    enabled: !!quoteId,
  });

  const updateMutation = useMutation({
    mutationFn: (status: QuoteStatus) => quotesApi.updateQuote(quoteId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      setActionError('');
    },
    onError: (e: any) => setActionError(extractError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => quotesApi.deleteQuote(quoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      navigate(`/projects/${quote?.project_id}`);
    },
    onError: (e: any) => setActionError(extractError(e)),
  });

  const handlePdf = async () => {
    if (!quote) return;
    try {
      await quotesApi.downloadPdf(quoteId, quote.quote_number);
    } catch (e: any) {
      setActionError(extractError(e));
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !quote) {
    return (
      <Box p={3}>
        <Alert severity="error">Quote not found or failed to load.</Alert>
      </Box>
    );
  }

  const isManager = user?.role === 'manager';
  const isSurveyor = user?.role === 'surveyor';
  const canEdit = isManager || isSurveyor;

  const statusColor = QUOTE_STATUS_COLORS[quote.status] ?? 'default';

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/projects" underline="hover" color="inherit">
          Projects
        </Link>
        <Link
          component={RouterLink}
          to={`/projects/${quote.project_id}`}
          underline="hover"
          color="inherit"
        >
          Project #{quote.project_id}
        </Link>
        <Typography color="text.primary">{quote.quote_number}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/projects/${quote.project_id}`)}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight={700}>
            {quote.quote_number}
          </Typography>
          <Chip
            label={QUOTE_STATUS_LABELS[quote.status]}
            color={statusColor}
            size="medium"
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={handlePdf}>
            Download PDF
          </Button>

          {canEdit && quote.status === QuoteStatus.DRAFT && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => updateMutation.mutate(QuoteStatus.SENT)}
              disabled={updateMutation.isPending}
            >
              Mark Sent
            </Button>
          )}

          {canEdit && quote.status === QuoteStatus.SENT && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<AcceptIcon />}
                onClick={() => updateMutation.mutate(QuoteStatus.ACCEPTED)}
                disabled={updateMutation.isPending}
              >
                Accept
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => updateMutation.mutate(QuoteStatus.REJECTED)}
                disabled={updateMutation.isPending}
              >
                Reject
              </Button>
            </>
          )}

          {isManager && quote.status !== QuoteStatus.ACCEPTED && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={8}>
          {/* Property & Heat Loss */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <HomeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Property & Heat Loss
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Floor Area
                  </Typography>
                  <Typography fontWeight={600}>{fmt(quote.floor_area_m2)} m²</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Age Band
                  </Typography>
                  <Typography fontWeight={600}>
                    {quote.construction_age_band ?? '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    EPC Rating
                  </Typography>
                  <Typography fontWeight={600}>{quote.epc_rating ?? '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    EPC Retrieved
                  </Typography>
                  <Typography fontWeight={600}>
                    {quote.epc_retrieved ? 'Yes' : 'Manual'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Design Heat Loss
                  </Typography>
                  <Typography fontWeight={600}>{fmt(quote.heat_loss_kw)} kW</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Recommended Pump
                  </Typography>
                  <Typography fontWeight={600}>
                    {fmt(quote.recommended_pump_kw)} kW
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Annual Heat Demand
                  </Typography>
                  <Typography fontWeight={600}>
                    {quote.annual_heat_demand_kwh
                      ? `${Math.round(quote.annual_heat_demand_kwh).toLocaleString()} kWh`
                      : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Line Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">VAT %</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quote.line_items.map((li) => (
                      <TableRow
                        key={li.id}
                        sx={li.is_labor ? { bgcolor: 'blue.50' } : undefined}
                      >
                        <TableCell>
                          <Typography variant="body2">{li.description}</Typography>
                          {li.is_labor && (
                            <Chip label="Labour" size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{li.quantity}</TableCell>
                        <TableCell align="right">{fmtCurrency(li.unit_price)}</TableCell>
                        <TableCell align="right">{li.vat_rate}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {fmtCurrency(li.line_total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          {quote.notes && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {quote.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={4}>
          {/* Financial Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Financial Summary
              </Typography>

              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal (ex. VAT)</Typography>
                  <Typography fontWeight={500}>{fmtCurrency(quote.subtotal_ex_vat)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">VAT</Typography>
                  <Typography fontWeight={500}>{fmtCurrency(quote.vat_amount)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={600}>Total (inc. VAT)</Typography>
                  <Typography fontWeight={600}>{fmtCurrency(quote.total_inc_vat)}</Typography>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">BUS Grant Deduction</Typography>
                  <Typography color="success.main" fontWeight={600}>
                    −{fmtCurrency(quote.bus_grant)}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    bgcolor: 'success.light',
                    borderRadius: 1,
                    p: 1.5,
                    mt: 0.5,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700} color="success.dark">
                      Customer Pays
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="success.dark">
                      {fmtCurrency(quote.total_after_grant)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              {quote.valid_until && (
                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                  Valid until:{' '}
                  {new Date(quote.valid_until).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Savings Projection */}
          {(quote.estimated_annual_saving_gbp || quote.estimated_co2_saving_kg) && (
            <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <EcoIcon color="success" />
                  <Typography variant="h6" fontWeight={600} color="success.dark">
                    Savings Projection
                  </Typography>
                </Stack>

                {quote.estimated_annual_saving_gbp !== undefined &&
                  quote.estimated_annual_saving_gbp !== null && (
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                      <SavingsIcon color="success" fontSize="large" />
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="success.dark">
                          {fmtCurrency(quote.estimated_annual_saving_gbp)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          estimated annual saving vs gas
                        </Typography>
                      </Box>
                    </Stack>
                  )}

                {quote.estimated_co2_saving_kg !== undefined &&
                  quote.estimated_co2_saving_kg !== null && (
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <HeatIcon color="success" fontSize="large" />
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="success.dark">
                          {Math.round(quote.estimated_co2_saving_kg).toLocaleString()} kg
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CO₂ saved per year
                        </Typography>
                      </Box>
                    </Stack>
                  )}

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                  Based on gas @ 7p/kWh vs ASHP SCOP 3.0 @ 28p/kWh. Actual savings may vary.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Meta */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Quote Info
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>Number:</strong> {quote.quote_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong>{' '}
                  {new Date(quote.created_at).toLocaleDateString('en-GB')}
                </Typography>
                {quote.updated_at && (
                  <Typography variant="body2">
                    <strong>Updated:</strong>{' '}
                    {new Date(quote.updated_at).toLocaleDateString('en-GB')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Quote</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete quote <strong>{quote.quote_number}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotePage;
